import { useQuery } from '@tanstack/react-query';
import api from '../api/client';
import { useRegion } from '../context/RegionContext';

export interface Store {
  storeId: number | string;
  storeName: string;
  [key: string]: unknown;
}

/**
 * Region-aware, cached, deduped store list.
 *
 * Multiple components calling this on the same page share a single fetch
 * (React Query dedupes by queryKey). React Strict Mode's double-invoke
 * doesn't cause a double fetch — React Query handles concurrent same-key
 * requests internally.
 *
 * Cache is keyed on region, so switching regions triggers a fresh fetch
 * for the new region but preserves the old region's data in memory for
 * instant switch-back.
 */
export function useStores(): Store[] {
  const { region } = useRegion();
  const query = useQuery<Store[]>({
    queryKey: ['stores', region],
    queryFn: async () => {
      const res = await api.get<Store[]>('/stores');
      return Array.isArray(res.data) ? res.data : [];
    },
    // Stores rarely change. Cache for 5 min before considering stale.
    staleTime: 5 * 60_000,
    enabled: Boolean(region),
  });
  // Preserve the pre-existing call shape (existing callers do `const stores = useStores()`)
  return query.data ?? [];
}

/**
 * Cross-region store list — fetches stores from every region and merges.
 * Used by the admin User page so the Assigned Stores dropdown can show
 * stores from both CA and US regardless of the admin's current region.
 * Deduplicates by storeId.
 */
export function useAllRegionStores(): Store[] {
  const ca = useQuery<Store[]>({
    queryKey: ['stores', 'CA'],
    queryFn: async () => {
      const res = await api.get<Store[]>('/stores', { headers: { 'X-Region': 'CA' } });
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 5 * 60_000,
  });
  const us = useQuery<Store[]>({
    queryKey: ['stores', 'US'],
    queryFn: async () => {
      const res = await api.get<Store[]>('/stores', { headers: { 'X-Region': 'US' } });
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 5 * 60_000,
  });
  const seen = new Set<string | number>();
  const merged: Store[] = [];
  for (const s of [...(ca.data ?? []), ...(us.data ?? [])]) {
    if (seen.has(s.storeId)) continue;
    seen.add(s.storeId);
    merged.push(s);
  }
  return merged;
}
